import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getUserProfile } from "./userService";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { getFileUrl } from "@shared/utils/fileUrl";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import UserUpdateForm, { UpdateImageProfile } from "./UserUpdateForm";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { useUpdateActiveStatus, useUpdateUserRole } from "./useUsers";
import ChangePasswordDialog from "./ChangePasswordDialog";

import BankingForm from "@features/banking/components/BankingForm";
import BankingValidationDialog from "@features/banking/components/BankingValidationDialog";
import AdminBankingEditForm from "@features/banking/components/AdminBankingEditForm";
import { useTranslation } from "react-i18next";
import {
  User,
  CreditCard,
  History,
  Settings,
  Shield,
  Mail,
  Phone,
  MapPin,
  Globe,
  Building2,
  CheckCircle2,
  AlertCircle,
  Edit3,
  Eye,
  Home,
  KeyRound,
  UserCog,
  TrendingUp,
  X,
  Clock,
  XCircle,
  FileCheck,
  UserCircle2,
  Headphones,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@shared/components/ui/avatar";
import { Button } from "@shared/components/ui/button";
import { Separator } from "@shared/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Switch } from "@shared/components/ui/switch";
import { format } from "date-fns";
import ImageViewer from "@shared/components/ImageViewer";
import { toast } from "sonner";
import AssignCollector from "./components/AssignCollector";
import AssignSupport from "./components/AssignSupport";
import { Input } from "@/shared/components";

const Account = () => {
  const auth = useAuth();
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailVerificationDialog, setEmailVerificationDialog] = useState(false);
  const [bankingValidationDialog, setBankingValidationDialog] = useState({
    isOpen: false,
    type: null, // "approve" or "reject"
  });
  const { id } = useParams();

  // Sync tabs with URL search param `?tab=` so users can link to a specific tab
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ["profile", "banking", "settings"];
  const paramTab = searchParams.get("tab");
  const initialTab = validTabs.includes(paramTab) ? paramTab : "profile";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Update state when the URL search param changes (back/forward buttons)
  useEffect(() => {
    const p = searchParams.get("tab");
    if (p && validTabs.includes(p) && p !== activeTab) {
      setActiveTab(p);
    }
    // intentionally only depend on searchParams
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Keep URL in sync when user changes tab
  useEffect(() => {
    const current = searchParams.get("tab") || "profile";
    if (activeTab !== current) {
      const next = new URLSearchParams(Array.from(searchParams.entries()));
      next.set("tab", activeTab);
      setSearchParams(next);
    }
    // intentionally only depend on activeTab
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Mutation hooks for admin/manager functions
  const { updateActiveStatus, isPending: isUpdatingStatus } =
    useUpdateActiveStatus();
  const { updateUserRole, isPending: isUpdatingRole } = useUpdateUserRole();

  // If there's an ID in the params, we're viewing someone else's profile
  const isViewingOtherUser = !!id;

  // Fetch user data if viewing someone else's profile
  const {
    data: profileUser,
    isLoading: isProfileLoading,
    refetch: refetchProfile,
  } = useQuery({
    queryKey: ["user", id],
    queryFn: () => getUserProfile(id),
    enabled: isViewingOtherUser,
  });

  // Use the profile user if viewing someone else, otherwise use auth user
  const currentUser = isViewingOtherUser ? profileUser : auth.user;

  // Check if current user is admin or manager (can modify other users)
  const canModifyUser =
    auth.user?.role === "admin" || auth.user?.role === "manager";
  const canModifyThisUser = isViewingOtherUser && canModifyUser;
  
  // Allow edit mode for: 1) current user's own account, 2) admin/manager viewing other users
  const canEdit = !isViewingOtherUser || canModifyUser;

  // Show loading if we're fetching profile data
  if (isViewingOtherUser && isProfileLoading) {
    return (
      <div className="flex items-center justify-center h-svh">
        <ThreeDotLoader />
      </div>
    );
  }

  const getVerificationStatus = () => {
    if (currentUser?.bankAccount && currentUser?.cni) {
      return {
        status: "verified",
        label: t("profile.verified"),
        variant: "success",
      };
    } else if (currentUser?.bankAccount || currentUser?.cni) {
      return {
        status: "partial",
        label: t("profile.partiallyVerified"),
        variant: "warning",
      };
    }
    return {
      status: "unverified",
      label: t("profile.unverified"),
      variant: "destructive",
    };
  };

  const verificationStatus = getVerificationStatus();

  // Handler functions for admin/manager actions
  const handleStatusChange = (isActive) => {
    const userId = isViewingOtherUser ? id : auth.user?.id;
    const toastId = toast.loading(t("messages.updating"));
    updateActiveStatus(
      { id: userId, values: { isActive } },
      {
        onSuccess: (data) => {
          toast.success(data.message || t("messages.userUpdated"), {
            id: toastId,
          });
          // Refetch profile data if viewing another user
          if (isViewingOtherUser && refetchProfile) {
            refetchProfile();
          }
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message || "Failed to update status",
            { id: toastId }
          );
        },
      }
    );
  };

  const handleRoleChange = (role) => {
    const userId = isViewingOtherUser ? id : auth.user?.id;
    const toastId = toast.loading(t("messages.updating"));
    updateUserRole(
      { id: userId, role },
      {
        onSuccess: (data) => {
          toast.success(data.message || "User role updated successfully", {
            id: toastId,
          });
          // Refetch profile data if viewing another user
          if (isViewingOtherUser && refetchProfile) {
            refetchProfile();
          }
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message || "Failed to update role",
            { id: toastId }
          );
        },
      }
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 p-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <Avatar className="h-20 w-20">
            <AvatarImage
              src={getFileUrl(currentUser?.profileImage)}
              alt={currentUser?.fullName}
            />
            <AvatarFallback className="text-lg font-semibold">
              {currentUser?.fullName?.charAt(0)?.toUpperCase() ||
                currentUser?.brandName?.charAt(0)?.toUpperCase() ||
                "U"}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-foreground">
              {currentUser?.fullName ||
                currentUser?.brandName ||
                t("profile.welcomeBack")}
            </h1>
            <div className="flex items-center gap-2">
              <Badge
                variant={verificationStatus.variant}
                className="flex items-center gap-1"
              >
                {verificationStatus.status === "verified" ? (
                  <CheckCircle2 className="h-3 w-3" />
                ) : (
                  <AlertCircle className="h-3 w-3" />
                )}
                {verificationStatus.label}
              </Badge>
              {currentUser?.email && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {currentUser.email}
                </Badge>
              )}
              {isViewingOtherUser && (
                <Badge variant="outline">
                  {t(`user.role.${currentUser?.role}`)}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v)}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            {t("profile.profile")}
          </TabsTrigger>
          <TabsTrigger value="banking" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            {t("profile.banking")}
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            {t("profile.settings")}
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6">
          <div className="space-y-6">
            {/* Enhanced Profile Overview Card */}
            <Card className="w-full">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>{t("profile.accountOverview")}</CardTitle>
                  </div>
                  {canEdit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditMode(!isEditMode)}
                      className="flex items-center gap-2"
                    >
                      {isEditMode ? (
                        <>
                          <Eye className="h-4 w-4" />
                          {t("profile.viewMode")}
                        </>
                      ) : (
                        <>
                          <Edit3 className="h-4 w-4" />
                          {t("profile.editMode")}
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <CardDescription>
                  {canEdit && isEditMode
                    ? t("profile.editYourInformation")
                    : t("profile.viewYourAccountDetails")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!isEditMode || !canEdit ? (
                  <div className="space-y-6">
                    {/* User Stats Section */}
                    <div className="flex justify-center">
                      <div className="flex items-center gap-8">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-primary">
                            {currentUser?.packagesCount || 0}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {t("profile.packages")}
                          </div>
                        </div>
                        <Separator orientation="vertical" className="h-12" />
                        <div className="text-center">
                          <div className="text-2xl font-bold text-primary">
                            {currentUser?.invoicesCount || 0}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {t("profile.invoices")}
                          </div>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Personal Information Section */}
                    <section>
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <User className="h-5 w-5" />
                        {t("profile.personalInformation")}
                      </h3>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="text-xs text-muted-foreground">
                              {t("user.email")}
                            </div>
                            <div className="font-medium">
                              {currentUser?.email || "-"}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="text-xs text-muted-foreground">
                              {t("user.phone")}
                            </div>
                            <div className="font-medium">
                              {currentUser?.phone || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    <Separator />

                    {/* Business Information Section */}
                    <section>
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Building2 className="h-5 w-5" />
                        {t("profile.businessInformation")}
                      </h3>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="text-xs text-muted-foreground">
                              {t("user.brandName")}
                            </div>
                            <div className="font-medium">
                              {currentUser?.brandName || "-"}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                          <Globe className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="text-xs text-muted-foreground">
                              {t("user.website")}
                            </div>
                            <div className="font-medium">
                              {currentUser?.website ? (
                                <a
                                  href={currentUser.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:underline"
                                >
                                  {currentUser.website}
                                </a>
                              ) : (
                                "-"
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="text-xs text-muted-foreground">
                              {t("user.city")}
                            </div>
                            <div className="font-medium">
                              {currentUser?.city || "-"}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                          <Home className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="text-xs text-muted-foreground">
                              {t("user.address")}
                            </div>
                            <div className="font-medium">
                              {currentUser?.address || "-"}
                            </div>
                          </div>
                        </div>
                        {currentUser?.neighborhood && (
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <div className="text-xs text-muted-foreground">
                                {t("user.neighborhood")}
                              </div>
                              <div className="font-medium">
                                {currentUser?.neighborhood}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>

                    <Separator />

                    {/* Account Details Section */}
                    <section>
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        {t("profile.accountDetails")}
                      </h3>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="p-3 rounded-lg bg-muted/50">
                          <div className="text-xs text-muted-foreground mb-1">
                            {t("profile.created")}
                          </div>
                          <div className="font-medium">
                            {currentUser?.createdAt
                              ? format(
                                  new Date(currentUser.createdAt),
                                  "dd/MM/y HH:mm"
                                )
                              : "-"}
                          </div>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/50">
                          <div className="text-xs text-muted-foreground mb-1">
                            {t("profile.lastUpdated")}
                          </div>
                          <div className="font-medium">
                            {currentUser?.updatedAt
                              ? format(
                                  new Date(currentUser.updatedAt),
                                  "dd/MM/y HH:mm"
                                )
                              : "-"}
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                ) : canEdit ? (
                  <div className="grid gap-6 lg:grid-cols-2">
                    {/* Profile Photo Card */}
                    <Card className="w-full">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-2">
                          <Settings className="h-5 w-5 text-muted-foreground" />
                          <CardTitle>
                            {t("setupAccount.editPhotoProfile")}
                          </CardTitle>
                        </div>
                        <CardDescription>
                          {t("profile.updateProfilePhoto")}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <UpdateImageProfile
                          initailData={getFileUrl(currentUser?.profileImage)}
                        />
                      </CardContent>
                    </Card>

                    {/* Quick Info Card */}
                    <Card className="w-full">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-2">
                          <Shield className="h-5 w-5 text-muted-foreground" />
                          <CardTitle>{t("profile.accountOverview")}</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-3">
                          {currentUser?.email && (
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                {currentUser.email}
                              </span>
                            </div>
                          )}
                          {currentUser?.phone && (
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                {currentUser.phone}
                              </span>
                            </div>
                          )}
                          {currentUser?.city && (
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                {currentUser.city}
                              </span>
                            </div>
                          )}
                          {currentUser?.website && (
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                              <Globe className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                {currentUser.website}
                              </span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Profile Information Form - Full Width */}
                    <Card className="w-full lg:col-span-2">
                      <CardHeader className="pb-4">
                        <div className="flex items-center gap-2">
                          <User className="h-5 w-5 text-muted-foreground" />
                          <CardTitle>
                            {t("setupAccount.profileInformation")}
                          </CardTitle>
                        </div>
                        <CardDescription>
                          {t("profile.personalInformation")}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <UserUpdateForm initailData={currentUser} />
                      </CardContent>
                    </Card>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Banking Tab */}
        <TabsContent value="banking" className="mt-6">
          <div className="space-y-6">
            <Card className="w-full">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>
                      {t("setupAccount.bankingInformation")}
                    </CardTitle>
                  </div>
                  {(canModifyUser && isViewingOtherUser) && (
                    <AdminBankingEditForm
                      userId={currentUser._id}
                      initialData={currentUser}
                    />
                  )}
                </div>
                <CardDescription>
                  {t("profile.bankInformation")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {currentUser?.bankAccount || currentUser?.cni ? (
                  <div className="space-y-6">
                    {/* Banking Information Grid */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-4">
                        <div className="p-4 rounded-lg border bg-card">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium text-sm">
                              {t("user.cni")}
                            </span>
                          </div>
                          <p className="text-lg font-mono">
                            {currentUser?.cni?.number || "-"}
                          </p>
                        </div>
                        <div className="p-4 rounded-lg border bg-card">
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium text-sm">
                              {t("user.bankName")}
                            </span>
                          </div>
                          <p className="text-lg">
                            {currentUser?.bankAccount?.bankName || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="md:col-span-1">
                        <div className="p-4 rounded-lg border bg-card h-full">
                          <div className="flex items-center gap-2 mb-2">
                            <CreditCard className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium text-sm">
                              {t("user.bankAccountNumber")}
                            </span>
                          </div>
                          <p className="text-lg font-mono break-all">
                            {currentUser?.bankAccount?.bankNumber || "-"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Documents Section */}
                    {(currentUser?.cni?.frontImage ||
                      currentUser?.cni?.backImage ||
                      currentUser?.bankAccount?.ribScan) && (
                      <>
                        <Separator />
                        <section>
                          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <Shield className="h-5 w-5" />
                            {t("profile.userDocuments")}
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {currentUser?.cni?.frontImage && (
                              <ImageViewer
                                path={currentUser.cni.frontImage}
                                label={t("profile.cniFront")}
                                alt={t("profile.cniFrontImage")}
                              />
                            )}
                            {currentUser?.cni?.backImage && (
                              <ImageViewer
                                path={currentUser.cni.backImage}
                                label={t("profile.cniBack")}
                                alt={t("profile.cniBackImage")}
                              />
                            )}
                            {currentUser?.bankAccount?.ribScan && (
                              <ImageViewer
                                path={currentUser.bankAccount.ribScan}
                                label={t("profile.ribScan")}
                                alt={t("profile.bankAccountDocument")}
                              />
                            )}
                          </div>
                        </section>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      {t("banking.noBankingInfo")}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {t("banking.noBankingInfoDescription")}
                    </p>
                    {!isViewingOtherUser && (
                      <BankingForm initialData={currentUser} />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6">
          <div className="space-y-6">
            {/* Admin Controls - Only show for admin/manager viewing other users */}
            {canModifyThisUser && (
              <Card className="border-orange-200 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-950/20">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <UserCog className="h-5 w-5 text-orange-600" />
                    <CardTitle className="text-orange-900 dark:text-orange-100">
                      {t("profile.adminSection")}
                    </CardTitle>
                  </div>
                  <CardDescription className="text-orange-700 dark:text-orange-300">
                    {t("profile.adminSectionDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-orange-800 dark:text-orange-200 mb-4 p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <strong>Note:</strong> You are viewing and can modify
                    another user's account as an {auth.user?.role}.
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Account Settings */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>{t("profile.accountSettings")}</CardTitle>
                </div>
                <CardDescription>
                  {t("profile.manageAccountSettings")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">
                      {t("profile.accountStatus")}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t("profile.currentAccountStatus")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={currentUser?.isActive ? "default" : "secondary"}
                    >
                      {currentUser?.isActive
                        ? t("profile.active")
                        : t("profile.inactive")}
                    </Badge>
                    {canModifyThisUser && (
                      <Switch
                        checked={currentUser?.isActive === true}
                        onCheckedChange={handleStatusChange}
                        disabled={isUpdatingStatus}
                      />
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">{t("profile.userRole")}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t("profile.currentUserRole")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {t(`user.role.${currentUser?.role}`)}
                    </Badge>
                    {canModifyThisUser && (
                      <Select
                        value={currentUser?.role}
                        onValueChange={handleRoleChange}
                        disabled={isUpdatingRole}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="client">
                            {t("user.role.client")}
                          </SelectItem>
                          <SelectItem value="livreur">
                            {t("user.role.livreur")}
                          </SelectItem>
                          <SelectItem value="collector">
                            {t("user.role.collector")}
                          </SelectItem>

                          <SelectItem value="accountant">
                            {t("user.role.accountant")}
                          </SelectItem>

                          <SelectItem value="clientSupport">
                            {t("user.role.clientSupport")}
                          </SelectItem>
                          <SelectItem value="manager">
                            {t("user.role.manager")}
                          </SelectItem>
                          {auth.user?.role === "admin" && (
                            <SelectItem value="admin">
                              {t("user.role.admin")}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                {(canModifyThisUser || (canEdit && isEditMode)) && (
                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Collector Assignment */}
                    <Card className="border-2 hover:border-primary/50 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="rounded-full bg-primary/10 p-2.5 mt-0.5">
                              <UserCircle2 className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1 space-y-2">
                              <div>
                                <h4 className="font-semibold text-base">
                                  {t("users.assignCollector")}
                                </h4>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {t("users.assignCollectorDescription")}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 pt-2">
                                {currentUser?.collector?.fullName ? (
                                  <>
                                    <Badge
                                      variant="secondary"
                                      className="font-medium"
                                    >
                                      {currentUser.collector.fullName}
                                    </Badge>
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  </>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="text-muted-foreground"
                                  >
                                    {t("users.noCollectorAssigned")}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <AssignCollector clientUser={currentUser} />
                        </div>
                      </CardContent>
                    </Card>

                    {/* Support Assignment */}
                    <Card className="border-2 hover:border-primary/50 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="rounded-full bg-blue-500/10 p-2.5 mt-0.5">
                              <Headphones className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="flex-1 space-y-2">
                              <div>
                                <h4 className="font-semibold text-base">
                                  {t("users.assignSupport")}
                                </h4>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {t("users.assignSupportDescription")}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 pt-2">
                                {currentUser?.assignedSupport?.fullName ? (
                                  <>
                                    <Badge
                                      variant="secondary"
                                      className="font-medium"
                                    >
                                      {currentUser.assignedSupport.fullName}
                                    </Badge>
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  </>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="text-muted-foreground"
                                  >
                                    {t("users.noSupportAssigned")}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <AssignSupport clientUser={currentUser} />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">
                      {t("profile.verificationLevel")}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t("profile.accountVerificationLevel")}
                    </p>
                  </div>
                  <Badge
                    variant={verificationStatus.variant}
                    className="flex items-center gap-1"
                  >
                    {verificationStatus.status === "verified" ? (
                      <CheckCircle2 className="h-3 w-3" />
                    ) : (
                      <AlertCircle className="h-3 w-3" />
                    )}
                    {verificationStatus.label}
                  </Badge>
                </div>

                {/* Banking Verification Section - Only for admin/manager */}
                {canModifyThisUser &&
                  currentUser?.bankingStatus &&
                  currentUser.bankingStatus !== "none" && (
                    <div className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium flex items-center gap-2">
                            <FileCheck className="h-4 w-4" />
                            {t("banking.verificationStatus")}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {t("banking.manageBankingVerification")}
                          </p>
                        </div>
                        <Badge
                          variant={
                            currentUser.bankingStatus === "approved"
                              ? "success"
                              : currentUser.bankingStatus === "pending"
                              ? "warning"
                              : "destructive"
                          }
                        >
                          {t(`banking.status.${currentUser.bankingStatus}`)}
                        </Badge>
                      </div>

                      {/* Banking Details */}
                      {currentUser.bankingStatus === "pending" &&
                        currentUser.pendingBankingInfo && (
                          <div className="bg-muted/50 p-3 rounded-md space-y-2 text-sm">
                            <div className="font-medium text-xs text-muted-foreground uppercase">
                              {t("banking.pendingInformation")}
                            </div>
                            {currentUser.pendingBankingInfo.cni?.number && (
                              <div className="flex items-center gap-2">
                                <Shield className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">
                                  CNI:
                                </span>
                                <span className="font-mono text-xs">
                                  {currentUser.pendingBankingInfo.cni.number}
                                </span>
                              </div>
                            )}
                            {currentUser.pendingBankingInfo.bankAccount
                              ?.bankName && (
                              <div className="flex items-center gap-2">
                                <Building2 className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">
                                  {t("user.bank")}:
                                </span>
                                <span className="text-xs">
                                  {
                                    currentUser.pendingBankingInfo.bankAccount
                                      .bankName
                                  }
                                </span>
                              </div>
                            )}
                            {currentUser.pendingBankingInfo.bankAccount
                              ?.bankNumber && (
                              <div className="flex items-center gap-2">
                                <CreditCard className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">
                                  {t("user.rib")}:
                                </span>
                                <span className="font-mono text-xs">
                                  {
                                    currentUser.pendingBankingInfo.bankAccount
                                      .bankNumber
                                  }
                                </span>
                              </div>
                            )}
                            {currentUser.bankingSubmittedAt && (
                              <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1 border-t">
                                <Clock className="h-3 w-3" />
                                {t("banking.submittedOn", {
                                  date: format(
                                    new Date(currentUser.bankingSubmittedAt),
                                    "PPP"
                                  ),
                                })}
                              </div>
                            )}
                          </div>
                        )}

                      {/* Verification History */}
                      {currentUser.bankingStatus === "approved" &&
                        currentUser.bankingApprovedAt && (
                          <div className="bg-green-50 dark:bg-green-950/20 p-3 rounded-md text-sm space-y-1">
                            <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                              <CheckCircle2 className="h-3 w-3" />
                              <span className="font-medium">
                                {t("banking.approvedBy")}:
                              </span>
                              <span>
                                {currentUser.bankingApprovedBy?.fullName ||
                                  t("common.unknown")}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                              <Clock className="h-3 w-3" />
                              {format(
                                new Date(currentUser.bankingApprovedAt),
                                "PPP 'at' p"
                              )}
                            </div>
                            {currentUser.bankingNotes && (
                              <div className="text-xs text-green-700 dark:text-green-300 pt-2 border-t border-green-200 dark:border-green-800">
                                <span className="font-medium">
                                  {t("banking.notes")}:
                                </span>{" "}
                                {currentUser.bankingNotes}
                              </div>
                            )}
                          </div>
                        )}

                      {currentUser.bankingStatus === "rejected" &&
                        currentUser.bankingRejectedAt && (
                          <div className="bg-red-50 dark:bg-red-950/20 p-3 rounded-md text-sm space-y-1">
                            <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                              <XCircle className="h-3 w-3" />
                              <span className="font-medium">
                                {t("banking.rejectedBy")}:
                              </span>
                              <span>
                                {currentUser.bankingRejectedBy?.fullName ||
                                  t("common.unknown")}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                              <Clock className="h-3 w-3" />
                              {format(
                                new Date(currentUser.bankingRejectedAt),
                                "PPP 'at' p"
                              )}
                            </div>
                            {currentUser.bankingRejectionReason && (
                              <div className="text-xs text-red-700 dark:text-red-300 pt-2 border-t border-red-200 dark:border-red-800">
                                <span className="font-medium">
                                  {t("banking.reason")}:
                                </span>{" "}
                                {currentUser.bankingRejectionReason}
                              </div>
                            )}
                            {currentUser.bankingNotes && (
                              <div className="text-xs text-red-700 dark:text-red-300">
                                <span className="font-medium">
                                  {t("banking.notes")}:
                                </span>{" "}
                                {currentUser.bankingNotes}
                              </div>
                            )}
                          </div>
                        )}

                      {/* Action Buttons for Pending Status */}
                      {currentUser.bankingStatus === "pending" && (
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() =>
                              setBankingValidationDialog({
                                isOpen: true,
                                type: "approve",
                              })
                            }
                            className="flex-1"
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            {t("banking.approve")}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() =>
                              setBankingValidationDialog({
                                isOpen: true,
                                type: "reject",
                              })
                            }
                            className="flex-1"
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            {t("banking.reject")}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                {/* Change Password Section - Only show for current user */}
                {(canModifyThisUser || canEdit) && (
                  <Card className="border-2 hover:border-primary/50 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="rounded-full bg-orange-500/10 p-2.5 mt-0.5">
                            <KeyRound className="h-5 w-5 text-orange-600" />
                          </div>
                          <div className="flex-1 space-y-2">
                            <div>
                              <h4 className="font-semibold text-base">
                                {t("user.changePassword")}
                              </h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                {t("user.changePasswordDescription", {
                                  userName:
                                    currentUser?.brandName ||
                                    currentUser?.fullName,
                                })}
                              </p>
                            </div>
                            {currentUser?.plainPassword && (
                              <div className="pt-2">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                                  <Shield className="h-3.5 w-3.5" />
                                  <span>{t("user.currentPassword")}</span>
                                </div>
                                <div className="relative">
                                  <Input
                                    type="text"
                                    value={currentUser.plainPassword}
                                    readOnly
                                    className="font-mono text-sm bg-muted/50"
                                  />
                                  {/* <Badge 
                                    variant="secondary" 
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs"
                                  >
                                    {t("user.temporary")}
                                  </Badge> */}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => setIsChangePasswordOpen(true)}
                          className="flex items-center gap-2 ml-4"
                        >
                          <Edit3 className="h-4 w-4" />
                          {t("user.changePassword")}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Change Password Dialog */}
      {(canModifyThisUser || canEdit) && currentUser && (
        <ChangePasswordDialog
          user={currentUser}
          open={isChangePasswordOpen}
          onOpenChange={setIsChangePasswordOpen}
        />
      )}

      {/* Banking Validation Dialog */}
      {canModifyThisUser && currentUser && (
        <BankingValidationDialog
          isOpen={bankingValidationDialog.isOpen}
          onClose={() => {
            setBankingValidationDialog({ isOpen: false, type: null });
            refetchProfile();
          }}
          user={{
            id: currentUser._id || currentUser.id,
            fullName: currentUser.fullName,
          }}
          type={bankingValidationDialog.type}
        />
      )}
    </div>
  );
};

export default Account;
